import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { Button, Box, Typography, Alert, IconButton } from '@mui/material'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { styled } from '@mui/material/styles'
import {
	LoginContainer,
	GridBackground,
	LoginPaper,
} from 'components/ApplicationStyles'
import LockResetIcon from '@mui/icons-material/LockReset'
import LockIcon from '@mui/icons-material/Lock'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { Link } from 'react-router-dom'
import { alertModal } from '../../util/util'
import axios from 'axios'

// 스타일링된 TextField 컴포넌트
const TechTextField = styled('input')(({ theme }) => ({
	width: '100%',
	padding: '16px',
	paddingLeft: '50px',
	paddingRight: '50px',
	fontSize: '16px',
	backgroundColor: 'rgba(255, 255, 255, 0.1)',
	border: '1px solid rgba(255, 255, 255, 0.3)',
	borderRadius: '12px',
	color: '#fff',
	outline: 'none',
	transition: 'all 0.3s ease',
	'&::placeholder': {
		color: 'rgba(255, 255, 255, 0.5)',
	},
	'&:focus': {
		borderColor: '#4C51D3',
		backgroundColor: 'rgba(255, 255, 255, 0.15)',
		boxShadow: '0 0 0 2px rgba(76, 81, 211, 0.3)',
	},
	'&:hover': {
		borderColor: 'rgba(255, 255, 255, 0.4)',
	},
}))

const InputContainer = styled(Box)({
	position: 'relative',
	marginBottom: '20px',
})

const InputIcon = styled(Box)({
	position: 'absolute',
	left: '16px',
	top: '50%',
	transform: 'translateY(-50%)',
	zIndex: 1,
	color: 'rgba(255, 255, 255, 0.7)',
})

const VisibilityButton = styled(IconButton)({
	position: 'absolute',
	right: '12px',
	top: '50%',
	transform: 'translateY(-50%)',
	color: 'rgba(255, 255, 255, 0.7)',
	'&:hover': {
		color: 'rgba(255, 255, 255, 0.9)',
		backgroundColor: 'rgba(255, 255, 255, 0.1)',
	},
})

const StyledButton = styled(Button)({
	background: 'linear-gradient(135deg, #4C51D3 0%, #6366f1 100%)',
	color: '#fff',
	padding: '14px 24px',
	borderRadius: '12px',
	textTransform: 'none',
	fontWeight: '600',
	fontSize: '16px',
	boxShadow: '0 4px 15px rgba(76, 81, 211, 0.4)',
	'&:hover': {
		background: 'linear-gradient(135deg, #3b40c7 0%, #4f46e5 100%)',
		boxShadow: '0 6px 20px rgba(76, 81, 211, 0.5)',
	},
	'&:disabled': {
		background: 'rgba(255, 255, 255, 0.1)',
		color: 'rgba(255, 255, 255, 0.3)',
		boxShadow: 'none',
	},
})

const PasswordReset = () => {
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const [isLoading, setIsLoading] = useState(false)
	const [message, setMessage] = useState('')
	const [messageType, setMessageType] = useState<'success' | 'error' | 'info'>(
		'success'
	)
	const [showPassword, setShowPassword] = useState(false)
	const [showPasswordConfirm, setShowPasswordConfirm] = useState(false)

	// 상태 관리
	const [resetData, setResetData] = useState({
		token: '',
		password: '',
		passwordConfirm: '',
	})

	// URL 파라미터에서 토큰 읽기
	useEffect(() => {
		const token = searchParams.get('token')
		if (token) {
			setResetData((prev) => ({
				...prev,
				token,
			}))
			setMessage('비밀번호 재설정 토큰이 확인되었습니다.')
			setMessageType('info')
		} else {
			setMessage(
				'올바르지 않은 접근입니다. 비밀번호 재설정 링크를 다시 확인해주세요.'
			)
			setMessageType('error')
		}
	}, [searchParams])

	// 입력 처리
	const handleInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const { name, value } = e.target
			setResetData((prev) => ({
				...prev,
				[name]: value,
			}))
			setMessage('')
		},
		[]
	)

	// 비밀번호 표시/숨김 토글
	const handleTogglePasswordVisibility = useCallback(() => {
		setShowPassword((prev) => !prev)
	}, [])

	const handleTogglePasswordConfirmVisibility = useCallback(() => {
		setShowPasswordConfirm((prev) => !prev)
	}, [])

	// 비밀번호 재설정 제출
	const handleSubmit = useCallback(
		async (e: React.FormEvent) => {
			e.preventDefault()

			if (!resetData.token) {
				setMessage('올바르지 않은 토큰입니다.')
				setMessageType('error')
				return
			}

			if (!resetData.password.trim()) {
				setMessage('새 비밀번호를 입력해주세요.')
				setMessageType('error')
				return
			}

			if (resetData.password.length < 6) {
				setMessage('비밀번호는 6자 이상이어야 합니다.')
				setMessageType('error')
				return
			}

			if (resetData.password !== resetData.passwordConfirm) {
				setMessage('비밀번호가 일치하지 않습니다.')
				setMessageType('error')
				return
			}

			setIsLoading(true)
			setMessage('')

			try {
				const response = await axios
					.post(`${process.env.REACT_APP_HOST}/auth/password/reset`, {
						token: resetData.token,
						password: resetData.password,
						confirmPassword: resetData.passwordConfirm,
					})
					.then((res) => res.data)
					.catch((error) => {
						if (
							error.response &&
							error.response.data &&
							error.response.data.message
						) {
							throw new Error(error.response.data.message)
						}
					})

				console.log(response)

				if (response.success) {
					setIsLoading(true)
					alertModal(response.data, 'success', () => {
						navigate('/')
					})
				}
			} catch (error) {
				console.error('비밀번호 재설정 오류:', error)
				setMessage(
					'비밀번호 재설정 중 오류가 발생했습니다. 토큰이 만료되었거나 올바르지 않을 수 있습니다.'
				)
				setMessageType('error')
			} finally {
				setIsLoading(false)
			}
		},
		[resetData, navigate]
	)

	// 비밀번호 일치 여부 확인
	const isPasswordMatch = useMemo(() => {
		return resetData.password && resetData.passwordConfirm
			? resetData.password === resetData.passwordConfirm
			: null
	}, [resetData.password, resetData.passwordConfirm])

	const iconColor = useMemo(() => ({ color: 'rgba(255, 255, 255, 0.7)' }), [])

	return (
		<LoginContainer maxWidth={false}>
			<GridBackground />
			<LoginPaper elevation={0}>
				<Box sx={{ width: '100%' }}>
					{/* 헤더 */}
					<Box sx={{ textAlign: 'center', mb: 4 }}>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								width: 80,
								height: 80,
								borderRadius: '50%',
								background: 'linear-gradient(135deg, #4C51D3 0%, #6366f1 100%)',
								margin: '0 auto 16px',
								boxShadow: '0 8px 25px rgba(76, 81, 211, 0.3)',
							}}
						>
							<LockResetIcon
								sx={{
									fontSize: 40,
									color: '#fff',
								}}
							/>
						</Box>

						<Typography
							variant="h4"
							sx={{
								color: '#fff',
								fontWeight: 'bold',
								mb: 1,
								textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
							}}
						>
							비밀번호 재설정
						</Typography>
						<Typography
							variant="body2"
							sx={{
								color: 'rgba(255, 255, 255, 0.7)',
							}}
						>
							새로운 비밀번호를 설정해주세요
						</Typography>
					</Box>

					{/* 메시지 표시 */}
					{message && (
						<Alert
							severity={messageType}
							sx={{
								mb: 3,
								backgroundColor:
									messageType === 'success'
										? 'rgba(76, 175, 80, 0.1)'
										: messageType === 'error'
										? 'rgba(244, 67, 54, 0.1)'
										: 'rgba(33, 150, 243, 0.1)',
								color:
									messageType === 'success'
										? '#4CAF50'
										: messageType === 'error'
										? '#f44336'
										: '#2196f3',
								border: `1px solid ${
									messageType === 'success'
										? 'rgba(76, 175, 80, 0.3)'
										: messageType === 'error'
										? 'rgba(244, 67, 54, 0.3)'
										: 'rgba(33, 150, 243, 0.3)'
								}`,
								borderRadius: '8px',
							}}
						>
							{message}
						</Alert>
					)}

					{/* 비밀번호 재설정 폼 */}
					{resetData.token && (
						<form onSubmit={handleSubmit}>
							{/* 새 비밀번호 입력 */}
							<InputContainer>
								<InputIcon>
									<LockIcon sx={iconColor} />
								</InputIcon>
								<TechTextField
									name="password"
									type={showPassword ? 'text' : 'password'}
									placeholder="새 비밀번호 (6자 이상)"
									value={resetData.password}
									onChange={handleInputChange}
									style={{
										borderColor:
											resetData.password.length > 0 && resetData.password.length < 6
												? '#f44336'
												: undefined,
									}}
								/>
								<VisibilityButton
									onClick={handleTogglePasswordVisibility}
									edge="end"
								>
									{showPassword ? <VisibilityOff /> : <Visibility />}
								</VisibilityButton>
							</InputContainer>

							{/* 비밀번호 확인 입력 */}
							<InputContainer>
								<InputIcon>
									<LockIcon sx={iconColor} />
								</InputIcon>
								<TechTextField
									name="passwordConfirm"
									type={showPasswordConfirm ? 'text' : 'password'}
									placeholder="새 비밀번호 확인"
									value={resetData.passwordConfirm}
									onChange={handleInputChange}
									style={{
										borderColor:
											isPasswordMatch === false
												? '#f44336'
												: isPasswordMatch === true
												? '#4CAF50'
												: undefined,
									}}
								/>
								<VisibilityButton
									onClick={handleTogglePasswordConfirmVisibility}
									edge="end"
								>
									{showPasswordConfirm ? <VisibilityOff /> : <Visibility />}
								</VisibilityButton>
								{isPasswordMatch === true && (
									<Box
										sx={{
											position: 'absolute',
											right: '50px',
											top: '50%',
											transform: 'translateY(-50%)',
										}}
									>
										<CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 20 }} />
									</Box>
								)}
							</InputContainer>

							{/* 비밀번호 규칙 안내 */}
							<Box sx={{ mb: 3 }}>
								<Typography
									variant="caption"
									sx={{
										color: 'rgba(255, 255, 255, 0.6)',
										display: 'block',
										mb: 1,
									}}
								>
									비밀번호 규칙:
								</Typography>
								<Typography
									variant="caption"
									sx={{
										color:
											resetData.password.length >= 6
												? '#4CAF50'
												: 'rgba(255, 255, 255, 0.5)',
									}}
								>
									• 6자 이상 입력
								</Typography>
							</Box>

							{/* 재설정 버튼 */}
							<StyledButton
								type="submit"
								fullWidth
								disabled={
									isLoading ||
									!resetData.password ||
									!resetData.passwordConfirm ||
									isPasswordMatch !== true
								}
								sx={{ mb: 3 }}
							>
								{isLoading ? '비밀번호 변경 중...' : '비밀번호 변경'}
							</StyledButton>
						</form>
					)}

					{/* 하단 링크 */}
					<Box sx={{ textAlign: 'center' }}>
						<Typography
							variant="body2"
							sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
						>
							비밀번호가 기억나셨나요?{' '}
							<Link
								to="/user/login"
								style={{
									color: '#4C51D3',
									textDecoration: 'none',
									fontWeight: 'bold',
								}}
							>
								로그인하기
							</Link>
						</Typography>
					</Box>

					{/* 추가 정보 */}
					<Typography
						variant="caption"
						sx={{
							color: 'rgba(255, 255, 255, 0.5)',
							textAlign: 'center',
							display: 'block',
							mt: 3,
						}}
					>
						© 2025 MindCline. All rights reserved.
					</Typography>
				</Box>
			</LoginPaper>
		</LoginContainer>
	)
}

export default PasswordReset
